import { useContext, useEffect } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import Swal from 'sweetalert2';

export async function confirmDiscardChanges({
  title = 'Hay cambios sin guardar',
  text = 'Si continuás, los cambios pendientes se descartarán.',
  confirmButtonText = 'Salir y descartar',
} = {}) {
  const result = await Swal.fire({
    title,
    text,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#6c757d',
    confirmButtonText,
    cancelButtonText: 'Cancelar y permanecer',
    reverseButtons: true,
    focusCancel: true,
  });
  return result.isConfirmed;
}

// BrowserRouter 6.8 no ofrece useBlocker. Este adaptador usa SweetAlert2
// para navegación interna y el aviso nativo obligatorio para recarga/cierre.
export default function usePendingChanges(dirty) {
  const { navigator } = useContext(UNSAFE_NavigationContext);

  useEffect(() => {
    if (!dirty) return undefined;
    const beforeUnload = (event) => {
      event.preventDefault();
      // Los navegadores modernos ignoran textos personalizados y muestran
      // su propio aviso de confirmación para recarga o cierre.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirty]);

  useEffect(() => {
    if (!dirty) return undefined;
    const originalPush = navigator.push;
    const originalReplace = navigator.replace;
    const wrap = (original) => (...args) => {
      confirmDiscardChanges().then(confirmed => {
        if (confirmed) original.apply(navigator, args);
      });
    };
    navigator.push = wrap(originalPush);
    navigator.replace = wrap(originalReplace);

    let index = window.history.state?.idx ?? 0;
    let reverting = false;
    let allowingPop = false;
    let allowedDelta = null;
    const pop = (event) => {
      const next = event.state?.idx ?? 0;
      if (allowingPop) {
        allowingPop = false;
        index = next;
        return;
      }
      if (reverting) {
        reverting = false;
        event.stopImmediatePropagation();
        if (allowedDelta !== null) {
          const delta = allowedDelta;
          allowedDelta = null;
          allowingPop = true;
          window.setTimeout(() => window.history.go(delta), 0);
        }
        return;
      }
      const delta = next - index;
      if (!delta) return;
      event.stopImmediatePropagation();
      reverting = true;
      window.history.go(-delta);
      confirmDiscardChanges().then(confirmed => {
        if (confirmed) {
          if (reverting) allowedDelta = delta;
          else {
            allowingPop = true;
            window.history.go(delta);
          }
        }
      });
    };
    window.addEventListener('popstate', pop, true);
    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
      window.removeEventListener('popstate', pop, true);
    };
  }, [dirty, navigator]);
}
